import { Injectable } from '@angular/core';

import { AccountBalanceService } from '@frontend/sports/account-balance/feature';
import { PlacementErrorType } from '@frontend/sports/types/betslip';
import { UserStateService } from '@frontend/sports/user/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, ActionCreator, Store } from '@ngrx/store';
import { concatMap, debounceTime, delay, distinctUntilChanged, filter, map, merge, startWith, switchMap, take, zip } from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { betslipSelector, betslipStageSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipState } from '../../../core/betslip-state';
import { BetslipPlaceBetActions } from '../../betplacement/actions';
import BetslipComboBetActions from '../../combo-bet/actions';
import { EditBetActions } from '../../edit-bet/actions';
import BetslipPickActions from '../../picks/actions';
import { addGroup, replaceGroup } from '../../picks/grouped-picks.actions';
import { RewardTokensActions } from '../../reward-tokens/actions';
import { RewardTokensEffects } from '../../reward-tokens/effects/reward-tokens.effects';
import SettingsActions from '../../settings/actions';
import BetslipSingleBetActions from '../../single-bet/actions';
import { BetslipStakeActions } from '../../stake/actions';
import BetslipSystemBetActions from '../../system-bet/actions';
import { BetslipTeaserActions } from '../../teaser-bet/actions';
import { BetslipTypesActions } from '../../types/actions';
import BetslipErrorsActions from '../actions';
import { selectHasAnyErrors } from '../selectors';
import BetslipErrorValidatorService from '../services/betslip-error-validator.service';
import { BetslipDoubleBetPreventionDialogService } from '../services/dialogs/betslip-double-bet-prevention-dialog.service';
import { IBetslipErrorsState } from '../state';

@Injectable()
export default class BetslipValidationEffects {
    // TODO we can extract the pick modify actions. And to clean the BPS pick error only if the pick with error is changed.
    private betslipManualChangeActions: ActionCreator[] = [
        BetslipPickActions.addPick,
        BetslipPickActions.removePick,
        BetslipPickActions.removeMultiplePicks,
        BetslipPickActions.setFixedPrice,
        BetslipPickActions.setStartingPrice,
        BetslipPickActions.togglePick,
        BetslipPickActions.togglePickForType,
        BetslipPickActions.toggleAllPicks,
        BetslipPickActions.selectPick,
        BetslipPickActions.deselectPick,
        BetslipPickActions.changeSelection,
        BetslipPickActions.changeLockState,
        BetslipPickActions.replacePick,
        replaceGroup,
        addGroup,

        BetslipActions.toggleBetslipDisplayMode,

        // LineSwitcherActions.addToSwapSelection,

        BetslipTypesActions.setSelectedType,
        BetslipTypesActions.setEachWay,

        // Single bet actions

        BetslipSingleBetActions.setSingleBetPickRewardToken,
        BetslipSingleBetActions.toggleSingleBetPickEachWay,
        BetslipSingleBetActions.setSingleBetPickEachWay,

        //Combo bet actions
        BetslipComboBetActions.setComboBetRewardToken,

        //Teaser bet actions
        BetslipTeaserActions.setTeaserBetRewardToken,
        BetslipTeaserActions.addUpdatePick,
        BetslipTeaserActions.removePick,
        BetslipTeaserActions.removePicks,

        // System bet actions
        BetslipSystemBetActions.toggleSystemEachWay,
        BetslipSystemBetActions.setSystemType,
        BetslipSystemBetActions.setSystemBetTypeStake,
        BetslipSystemBetActions.setSystemEachWay,

        // Validate after setting odd acceptance, to remove odds changed errors if any.
        SettingsActions.setOddAcceptance,

        BetslipStakeActions.notifyStakeChanges,

        // Edit bet actions
        EditBetActions.fillEditMyBet,
        EditBetActions.removePick,
        EditBetActions.swapPick,
        EditBetActions.undoRemovePick,
        EditBetActions.addPickToContainer,
        EditBetActions.removeFromContainer,
        EditBetActions.addPickToSwap,
        EditBetActions.updatePickToSwap,
        EditBetActions.setEditMyBetAddedStake,
        EditBetActions.cancelAddSelectionMode,
        EditBetActions.confirmAddingStake,
        EditBetActions.confirmAddSelectionMode,

        // Reward tokens actions
        RewardTokensActions.setToken,
        RewardTokensActions.removeTokensByType,

        BetslipPlaceBetActions.setAuthentication,
    ];

    private betslipChangeActions: ActionCreator[] = [
        ...this.betslipManualChangeActions,
        BetslipPickActions.acceptCurrentPrice,
        BetslipPickActions.updateV1Picks,
        BetslipPickActions.updateV2Picks,
        BetslipPickActions.updateGroupPicks,
        EditBetActions.editBetUpdated,
        RewardTokensActions.selectToken,
        RewardTokensActions.notifyEligibilityUpdated,
    ];

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private validationService: BetslipErrorValidatorService,
        // private editBetUndoCashOutDialogService: EditBetUndoCashOutDialogService,
        private betslipDoubleBetPreventionDialogService: BetslipDoubleBetPreventionDialogService,
        private userStateService: UserStateService,
        private accountBalanceService: AccountBalanceService,
    ) {}

    //Disable placement button when stake changes until the next validation cycle finishes excutions
    disablePlaceButtonOnStakeChanges$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.setStake),
            map(() => BetslipPlaceBetActions.validatePlaceButton({ isValid: false })),
        );
    });

    validateOnUserStateChange$ = createEffect(() => {
        return merge(
            this.userStateService.userState$().pipe(
                map((userState) => userState.isAuthenticated && userState.walletLoaded),
                distinctUntilChanged(),
                filter((isAuthenticated) => isAuthenticated),
            ),
            this.accountBalanceService.availableWallets,
        ).pipe(map(() => BetslipErrorsActions.validateBetslip()));
    });

    //Enable placement button after validation cycle finishes excutions, add delay as a buffer
    enablePlaceButtonAfterValidation$ = createEffect(() => {
        return merge([this.accountBalanceService.availableWallets, this.actions$.pipe(ofType(BetslipTypesActions.setStake))]).pipe(
            switchMap((_) =>
                zip([
                    this.actions$.pipe(ofType(BetslipErrorsActions.picksErrorsCheckFinished)),
                    this.actions$.pipe(ofType(BetslipErrorsActions.betslipErrorsCheckFinished)),
                ]),
            ),
            delay(2),
            map(() => BetslipPlaceBetActions.validatePlaceButton({ isValid: true })),
        );
    });

    /**
     * Do a betslip validation when betslip is changed.
     */
    validateBetslipOnChangeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(...this.getManualActionsExcludingTokenValidationTriggers()),
            debounceTime(5), // Debounce validation, so no to be triggered after every action and to clean the noise.
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => !!betslipState.types.base.currentSelectedType), // No need to validate when no type, we need to have this in case of empty slip after populateFromStorage
            switchMap(([_, betslipState]) => {
                return this.validationService.validate$(betslipState).pipe(switchMap((errorState) => this.createValidateActions(errorState)));
            }),
        );
    });

    /**
     * Handle manual validate execute. It is not debounced.
     */
    handleValidateBetslipEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.validateBetslip),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            switchMap(([_, betslipState]) => {
                return this.validationService.validate$(betslipState).pipe(
                    take(1),
                    switchMap((errorState) => this.createValidateActions(errorState)),
                );
            }),
        );
    });

    /**
     * Clean all server errors on manual changes.
     */
    removeServerEventsOnManualActionsEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(...this.betslipManualChangeActions),
            //clean the noise, we don't need to spam the action stream with these two actions on every manual change, this is a safe usage.
            debounceTime(5),
            concatLatestFrom(() => this.store.select(betslipStageSelector)),
            filter(([_, betslipStage]) => betslipStage !== BetslipState.Result),
            concatLatestFrom(() => this.store.select(selectHasAnyErrors)),
            filter(([_, hasErrors]) => hasErrors),
            concatMap(() => [BetslipErrorsActions.cleanManualRemoveErrors(), BetslipErrorsActions.removeOverallMaxWinPerUserExceededError()]),
        );
    });

    handleDoubleBetPreventionErrorEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.placeBetResponseErrors),
            filter(
                (errors) =>
                    errors.betslipErrors.some((err) => err.type === PlacementErrorType.DoubleBetPrevention) ||
                    Object.values(errors.slipErrors)
                        .flatMap((e) => Object.values(e))
                        .some((err) => err.type === PlacementErrorType.DoubleBetPrevention),
            ),
            switchMap((_) => {
                return this.betslipDoubleBetPreventionDialogService.show().pipe(
                    map(() => BetslipPlaceBetActions.disableDoubleBetPrevention()),
                    startWith(BetslipErrorsActions.removeDoubleBetPreventionError()),
                );
            }),
        );
    });

    /*
		handleUndoCashOutFailedErrorEffect$ = createEffect(() =>
			this.actions$.pipe(
				ofType(BetslipErrorsActions.placeBetResponseErrors),
				concatMap(action => {
					if (action.payload.betslipErrors.some(e => e instanceof UndoCashOutFailedError)) {
						return from(this.editBetUndoCashOutDialogService.show()).pipe(map(() => BetslipEditBetActions.exitEditBet()));
					}
					return [];
				})
			)
		);*/

    private createValidateActions(errorState: IBetslipErrorsState): Action[] {
        const pickErrors = errorState.pickErrors;
        const betslipErrors = errorState.betslipErrors;
        const typeSlipErrors = errorState.betslipTypeErrors;
        const slipErrors = errorState.slipErrors;

        return [
            BetslipErrorsActions.picksErrorsCheckFinished({ errors: pickErrors }),
            BetslipErrorsActions.betslipErrorsCheckFinished({
                errors: betslipErrors,
                typeSlipErrors,
                slipErrors,
            }),
        ];
    }

    //This function was added to avoid any racing conditions between both effects validateBetslipOnChangeEffect$ and RewardTokensEffects.betslipContextChangedEffect$
    //Previously the same actions were handled by both effects, now validateBetslipOnChangeEffect will only ignore RewardTokensEffects.Betslip_Context_Change_Actions to make sure that it doesn't run before RewardTokensActions.checkForErrors
    private getManualActionsExcludingTokenValidationTriggers() {
        return [...this.betslipChangeActions.filter((t) => !RewardTokensEffects.BETSLIP_CONTEXT_CHANGE_ACTIONS.some((y) => y.type === t.type))];
    }
}
